/**
 * Compatibility layer for the new VersionWatcher architecture.
 *
 * All real logic lives in `src/lib/version-watcher.ts`. This module keeps the
 * previous public API stable for callers that we explicitly do NOT touch in
 * this refactor (RealExamMode, SubscriptionRoute, RefreshApp). Each export
 * routes through the new watcher / cleanCachesAndReload primitive.
 */

import type { QueryClient } from "@tanstack/react-query";
import { getRunningVersion } from "@/lib/version-source";
import {
  cleanCachesAndReload,
  versionWatcher,
  type ManifestPayload,
} from "@/lib/version-watcher";

export const APP_VERSION = getRunningVersion();
export const EMERGENCY_REFRESH_ROUTE = "/refresh-app";

export function getRunningBundleVersion(): string {
  return getRunningVersion();
}

/** Legacy probe — the new system always has its UI mounted, so this is true. */
export function getUpdateNotifierMounted(): boolean {
  return true;
}

export async function fetchServerManifestVersion(): Promise<{
  version: string | null;
  build: string | null;
  timestamp: string | null;
}> {
  const res = await fetch(`/manifest-version.json?t=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
  });
  if (!res.ok) throw new Error(`manifest_version_http_${res.status}`);
  const data = (await res.json()) as Partial<ManifestPayload> & { build?: string };
  return {
    version: typeof data?.version === "string" ? data.version : null,
    build: typeof data?.build === "string" ? data.build : null,
    timestamp: typeof data?.timestamp === "string" ? data.timestamp : null,
  };
}

export function openEmergencyRefreshRoute(
  returnTo = window.location.pathname + window.location.search,
  reason = "manual"
) {
  const url = new URL(EMERGENCY_REFRESH_ROUTE, window.location.origin);
  url.searchParams.set("returnTo", returnTo);
  url.searchParams.set("reason", reason);
  window.location.assign(url.toString());
}

export function hasPendingRefresh(): boolean {
  return versionWatcher.getState().pendingReload;
}

export function clearPendingRefresh(): void {
  // Watcher recomputes pendingReload on each poll. No-op for compatibility.
}

interface SafeRefreshOptions {
  aggressive?: boolean;
  reason?: string;
  redirectPath?: string;
  force?: boolean;
}

/**
 * Compatibility shim. Performs a cache-safe hard reload using the new
 * cleanCachesAndReload primitive. Always preserves sb-* + subscription_cache.
 */
export async function safeCacheRefresh(
  _queryClient?: QueryClient,
  options: SafeRefreshOptions = {}
): Promise<void> {
  await cleanCachesAndReload({
    reason: options.reason || "compat-safeCacheRefresh",
    redirectPath: options.redirectPath,
  });
}

export async function safeCacheRefreshWithTimeout(
  queryClient?: QueryClient,
  options: SafeRefreshOptions = {}
): Promise<void> {
  return safeCacheRefresh(queryClient, options);
}

/**
 * Legacy boot-time check. The new system handles boot inside the
 * VersionWatcher (and the slim boot-gate in index.html), so this becomes a
 * no-op that simply asks the watcher to check.
 */
export async function ensureFreshVersion(_queryClient?: QueryClient): Promise<boolean> {
  try {
    await versionWatcher.forceCheck({ reason: "ensureFreshVersion-compat" });
  } catch {
    /* ignore */
  }
  return false;
}

export { cleanCachesAndReload };
